import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import CircleOption from '../CircleOption';
import { translate } from '../../services/L10n';
import { t } from '../../translations';
import styles from './ProductMenuColorFilter.module.less';
import {
  selectCurrentColorFilterName,
  selectProductMenuFilter,
  selectSelectableColorsAsArray,
} from '../../state/productMenu/productMenuSelectors';
import { selectIsLandscape } from '../../state/userAgent/userAgentSelectors';
import { thunkSetColorFilter } from '../../state/productMenu/productMenuThunks';
import { ColorCombination } from '../../state/productMenu/productMenuTypes';
import { KompisText } from '@inter-ikea-kompis/react-components';
import { SkapaTheme } from '@inter-ikea-kompis/themes/lib';
import localStatisticsReporter from '../../services/statistics/insights/custom/local/localStatisticsReporter';

export const ProductMenuColorFilter = () => {
  const currentColorFilterName = useSelector(selectCurrentColorFilterName);
  const currentFilter = useSelector(selectProductMenuFilter);
  const selectableColors = useSelector(selectSelectableColorsAsArray);
  const isLandscape = useSelector(selectIsLandscape);

  const dispatch = useDispatch();
  const setColorFilter = (color: ColorCombination) => () => {
    dispatch(thunkSetColorFilter(color.name));
    localStatisticsReporter.reportProductMenuSubFilterInteraction(
      currentFilter.name,
      'color',
      color.name
    );
  };

  const tooltips: { [key: string]: string } = {
    white: translate(t.TOOLTIP_WHITE),
    wood: translate(t.TOOLTIP_WOOD),
    oak: translate(t.TOOLTIP_OAK_PATTERNED),
    black: translate(t.TOOLTIP_BLACK),
    anthracite: translate(t.TOOLTIP_ANTHRACITE),
    grey: translate(t.TOOLTIP_GREY),
    grey_oak: `${translate(t.TOOLTIP_GREY)}/${translate(
      t.TOOLTIP_OAK_PATTERNED
    )}`,
    grey_green: translate(t.TOOLTIP_GREY_GREEN),
    black_wood: `${translate(t.TOOLTIP_WOOD)}/${translate(t.TOOLTIP_BLACK)}`,
    dark_grey: translate(t.TOOLTIP_DARK_GREY),
    bamboo: translate(t.TOOLTIP_BAMBOO),
  };

  /**
   * Render header label
   * @returns {false|JSX.Element}
   */
  const renderHeaderLabel = () =>
    isLandscape && (
      <KompisText theme={SkapaTheme}>{`${translate(t.COLOUR)}:`}</KompisText>
    );

  /**
   * Render selectable colors
   */
  const renderSelectableColors = () =>
    selectableColors.map(color => (
      <CircleOption
        key={color.name}
        name={color.name}
        selected={color.name === currentColorFilterName}
        onClick={setColorFilter(color)}
        tooltipContent={tooltips[color.name]}
      />
    ));

  return (
    <div className={styles.wrapper}>
      {renderHeaderLabel()}
      <div data-testid="color-options" className={styles.optionsWrapper}>
        {renderSelectableColors()}
      </div>
    </div>
  );
};

export default ProductMenuColorFilter;
